import Konva from 'konva';
import { Layer } from 'react-konva';
import React, { useCallback, useEffect, useRef } from 'react';
import { getViewport, getTempState, getProjectState } from '@starflow/app/store';

export const GUIDE_LINE_COLOR = '#558DF8';
export const GUIDELINE_OFFSET = 5;

export const DragSnap = () => {
  const layerRef = useRef(null);

  const getLineGuideStops = useCallback((skipShape) => {
    const stage = skipShape.getStage();

    if (!stage) return { vertical: [], horizontal: [] };

    // we can snap to stage borders and the center of the stage
    const vertical = [0, stage.width()];
    const horizontal = [0, stage.height()];
    const elements = stage?.findOne('#elementsLayer').children;

    // and we snap over edges and center of each object on the canvas
    elements.forEach((guideItem) => {
      if (guideItem === skipShape) {
        return;
      }
      const box = guideItem.getClientRect();
      // and we can snap to all edges of shapes
      vertical.push(box.x, box.x + box.width, box.x + box.width / 2);
      horizontal.push(box.y, box.y + box.height, box.y + box.height / 2);
    });
    return {
      vertical,
      horizontal,
    };
  }, []);

  const getObjectSnappingEdges = useCallback((node) => {
    const box = node.getClientRect();
    const absPos = node.absolutePosition();

    return {
      vertical: [
        {
          guide: Math.round(box.x),
          offset: Math.round(absPos.x - box.x),
          snap: 'start',
        },
        {
          guide: Math.round(box.x + box.width / 2),
          offset: Math.round(absPos.x - box.x - box.width / 2),
          snap: 'center',
        },
        {
          guide: Math.round(box.x + box.width),
          offset: Math.round(absPos.x - box.x - box.width),
          snap: 'end',
        },
      ],
      horizontal: [
        {
          guide: Math.round(box.y),
          offset: Math.round(absPos.y - box.y),
          snap: 'start',
        },
        {
          guide: Math.round(box.y + box.height / 2),
          offset: Math.round(absPos.y - box.y - box.height / 2),
          snap: 'center',
        },
        {
          guide: Math.round(box.y + box.height),
          offset: Math.round(absPos.y - box.y - box.height),
          snap: 'end',
        },
      ],
    };
  }, []);

  const getGuides = useCallback((lineGuideStops, itemBounds) => {
    const resultV = [];
    const resultH = [];

    lineGuideStops.vertical.forEach((lineGuide) => {
      itemBounds.vertical.forEach((itemBound) => {
        const diff = Math.abs(lineGuide - itemBound.guide);
        if (diff < GUIDELINE_OFFSET) {
          resultV.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          });
        }
      });
    });

    lineGuideStops.horizontal.forEach((lineGuide) => {
      itemBounds.horizontal.forEach((itemBound) => {
        const diff = Math.abs(lineGuide - itemBound.guide);
        if (diff < GUIDELINE_OFFSET) {
          resultH.push({
            lineGuide: lineGuide,
            diff: diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          });
        }
      });
    });

    const guides = [];

    const minV = resultV.sort((a, b) => a.diff - b.diff)[0];
    const minH = resultH.sort((a, b) => a.diff - b.diff)[0];

    if (minV) {
      guides.push({
        lineGuide: minV.lineGuide,
        offset: minV.offset,
        orientation: 'V',
        snap: minV.snap,
      });
    }

    if (minH) {
      guides.push({
        lineGuide: minH.lineGuide,
        offset: minH.offset,
        orientation: 'H',
        snap: minH.snap,
      });
    }

    return guides;
  }, []);

  const drawGuides = useCallback((guides, layer) => {
    const scale = layer.getAbsoluteScale().x;
    const lineWidth = (layer.width() * 2) / scale;
    const lineHeight = (layer.height() * 2) / scale;

    guides.forEach((lg) => {
      if (lg.orientation === 'H') {
        const line = new Konva.Line({
          points: [-lineHeight, 0, lineHeight, 0],
          stroke: GUIDE_LINE_COLOR,
          strokeWidth: Math.ceil(1 / scale),
          name: 'guideLine',
          dash: [4 / scale, 6 / scale],
        });
        layer.add(line);
        line.absolutePosition({
          x: 0,
          y: lg.lineGuide,
        });
      } else if (lg.orientation === 'V') {
        const line = new Konva.Line({
          points: [0, -lineWidth, 0, lineWidth],
          stroke: GUIDE_LINE_COLOR,
          strokeWidth: Math.ceil(1 / scale),
          name: 'guideLine',
          dash: [4 / scale, 6 / scale],
        });
        layer.add(line);
        line.absolutePosition({
          x: lg.lineGuide,
          y: 0,
        });
      }
    });
  }, []);

  const handleDragMove = useCallback((e) => {
    // 暂时先只支持图片，不支持Transformer
    const parent = e.target.getParent();

    if (!parent || parent.id() !== 'elementsLayer') return;

    const selection = getProjectState('selection');

    if (selection.length > 1 && selection.some((item) => item === e.target.name())) return;

    // clear all previous lines on the screen
    layerRef.current.find('.guideLine').forEach((l) => l.destroy());

    // find possible snapping lines
    const lineGuideStops = getLineGuideStops(e.target);

    // find snapping points of current object
    const itemBounds = getObjectSnappingEdges(e.target);

    // now find where can we snap current object
    const guides = getGuides(lineGuideStops, itemBounds);

    // do nothing if no snapping
    if (!guides.length) return;

    drawGuides(guides, layerRef.current);

    const absPos = e.target.absolutePosition();
    // now force object position
    guides.forEach((lg) => {
      switch (lg.snap) {
        case 'start': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
        case 'center': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
        case 'end': {
          switch (lg.orientation) {
            case 'V': {
              absPos.x = lg.lineGuide + lg.offset;
              break;
            }
            case 'H': {
              absPos.y = lg.lineGuide + lg.offset;
              break;
            }
          }
          break;
        }
      }
    });

    e.target.absolutePosition(absPos);

    const viewport = getViewport();

    if (e.target.getAttr('role') === 'generator') {
      const generatorOverlayer = document.getElementById(`generator-${e.target.name()}`);
      if (generatorOverlayer) {
        generatorOverlayer.style.left = e.target.x() * viewport.scale + viewport.x + 'px';
        generatorOverlayer.style.top = e.target.y() * viewport.scale + viewport.y + 'px';
      }
    }

    if (e.target.getAttr('roleType') === 'image') {
      const imageOverlayer = document.getElementById(`image-overlay-${e.target.name()}`);
      if (imageOverlayer) {
        imageOverlayer.style.left = e.target.x() * viewport.scale + viewport.x + 'px';
        imageOverlayer.style.top = e.target.y() * viewport.scale + viewport.y + 'px';
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = useCallback(() => {
    layerRef.current.find('.guideLine').forEach((l) => l.destroy());
  }, []);

  useEffect(() => {
    const stage = layerRef.current.getStage();

    if (!stage) return;

    stage.on('dragmove', handleDragMove);
    stage.on('dragend', handleDragEnd);

    return () => {
      stage.off('dragmove', handleDragMove);
      stage.off('dragend', handleDragEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Layer ref={layerRef} />;
};
